'use client';

import React, { useId } from 'react';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  name?: string;
  id?: string;
  minSelection?: number;
  className?: string;
}

export const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options,
      value,
      onChange,
      label,
      helperText,
      error,
      disabled = false,
      name,
      id: providedId,
      minSelection,
      className,
    },
    ref,
  ) => {
    const generatedId = useId();
    const rootId = providedId ?? (name ? `multiselect-${name}` : generatedId);
    const labelId = `${rootId}-label`;
    const helperId = `${rootId}-helper`;
    const errorId = `${rootId}-error`;

    const handleToggle = (optionValue: string) => {
      if (disabled) return;
      const isSelected = value.includes(optionValue);
      if (isSelected && minSelection != null && value.length <= minSelection) return;
      const next = isSelected ? value.filter((v) => v !== optionValue) : [...value, optionValue];
      onChange(next);
    };

    return (
      <div
        ref={ref}
        className={cn('space-y-1', className)}
        role="group"
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={error ? errorId : helperText ? helperId : undefined}
      >
        {label && (
          <label
            id={labelId}
            className="text-foreground block text-sm font-medium"
            htmlFor={undefined}
          >
            {label}
          </label>
        )}
        <div className="flex flex-wrap gap-3">
          {options.map((option) => {
            const checked = value.includes(option.value);
            const optionId = `${rootId}-${option.value}`;
            return (
              <label
                key={option.value}
                className={cn(
                  'border-border bg-background text-foreground inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                  !disabled && 'hover:border-ring/50',
                  disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <Checkbox
                  id={optionId}
                  name={name}
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={() => handleToggle(option.value)}
                  aria-label={option.label}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
        {error && (
          <p id={errorId} className="text-sm text-red-600">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="text-muted text-sm">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);
MultiSelect.displayName = 'MultiSelect';
